// Drizzle table definitions for the bbbk Postgres schema.
// Mirrors docs/decision/data-model-skeleton.md SQL one-for-one.
//
// Naming: table names snake_case to match the SQL. Inferred row types
// re-exported from ./types.ts as PascalCase singular (Booking, Owner, …).

import { sql } from "drizzle-orm";
import {
  bigint,
  boolean,
  check,
  customType,
  date,
  index,
  uniqueIndex,
  integer,
  jsonb,
  numeric,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uuid,
} from "drizzle-orm/pg-core";

// CITEXT extension column — case-insensitive text at the column level.
// Requires `CREATE EXTENSION citext` in the migration (added by hand
// in the generated SQL — drizzle-kit doesn't emit extension DDL).
const citext = customType<{ data: string }>({
  dataType() {
    return "citext";
  },
});

// --- owners ------------------------------------------------------------

export const owners = pgTable("owners", {
  id: text("id").primaryKey(),
  address: text("address"),
  notes: text("notes"),
  // "Do not mix" — this household's dogs must be kept apart from others. Drives a
  // red row indicator on the household's bookings (do-not-mix).
  doNotMix: boolean("do_not_mix").notNull().default(false),
  // {"address": 42552} — the household address is customer-written too, since
  // writeDetails does UPDATE owners SET address (0033).
  confirmedBy: jsonb("confirmed_by"),
  // Household default nightly rate. From the address-book Rate, later overridden
  // by the household's most-recent booking rate (data-reimport-field-map.md).
  defaultRate: numeric("default_rate", { precision: 10, scale: 2 }),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
  // Bumped on every row change by the set_updated_at BEFORE UPDATE trigger
  // (migration 0016) — the "Last updated" column's sort source. Backfilled to
  // created_at for pre-existing rows.
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
  archivedAt: timestamp("archived_at", { withTimezone: true }),
});

// --- owner_contacts ---------------------------------------------------

export const ownerContacts = pgTable(
  "owner_contacts",
  {
    id: integer("id").primaryKey().generatedByDefaultAsIdentity(),
    ownerId: text("owner_id")
      .notNull()
      .references(() => owners.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").notNull(),
    name: citext("name").notNull(),
    // ADDED BESIDE `name`, never instead of it (migration 0024). `name` stays
    // citext NOT NULL with its index, and remains what every existing reader
    // selects — five of them, named in portal-foundations AC5a. These two are
    // backfilled by splitting on the first space and nothing reads them until
    // household-app-access, which is what makes the split retryable: drop both,
    // re-apply 0024, and they are recomputed from a `name` that never left.
    firstName: citext("first_name"),
    lastName: citext("last_name"),
    phone: text("phone"),
    email: citext("email"),
    // WHEN THIS PERSON FINISHED REGISTERING in the customer app (migration
    // 0032). The allowlist entry is the access; this is the fact that they
    // confirmed who they are and checked their dogs. Null until the dogs step
    // submits (customer-sign-in-registration AC28), written once (AC29), and
    // the difference between *Signing up* and *Registered* on the desk (AC1).
    registeredAt: timestamp("registered_at", { withTimezone: true }),
    // {"firstName": 42552, "lastName": 42552} — what this person told us about
    // themselves at *Your details*. NOT email: a registered contact's email is
    // their sign-in identity and is locked outright (0033).
    confirmedBy: jsonb("confirmed_by"),
  },
  (t) => [
    index("idx_owner_contacts_owner").on(t.ownerId),
    index("idx_owner_contacts_name").on(t.name),
    // The portal looks a household up by the number the customer signs in with
    // (§7). The desk's own search still goes through `name`.
    index("idx_owner_contacts_phone").on(t.phone),
  ],
);

// --- dogs --------------------------------------------------------------

export const dogs = pgTable(
  "dogs",
  {
    id: text("id").primaryKey(),
    ownerId: text("owner_id")
      .notNull()
      .references(() => owners.id, { onDelete: "restrict" }),
    name: citext("name").notNull(),
    breed: text("breed"),
    size: text("size").notNull(),
    // vaccines_due is TEXT not DATE — historic data is "NOV", "MARCH 27",
    // "MA Y 25". See data-model-skeleton.md. (kennel_cough dropped — empty on
    // every row; one vaccination field per dog.)
    vaccinesDue: text("vaccines_due"),
    // Kennel cough, same shape and same parser as vaccines_due — TEXT because
    // the historic data is "NOV" and "MARCH 27" (migration 0025). Written only
    // by the desk's Accept on a card proof, in vaccination-cards.
    kcDue: text("kc_due"),
    meds: text("meds"),
    notes: text("notes"),
    // THE CUSTOMER'S OWN ANSWER, per dog — they are asked once per dog and it
    // was being flattened straight onto owners.do_not_mix. NULL MEANS NOBODY
    // HAS ANSWERED, which is not the same as *mixes well*; every dog the desk
    // typed predates the portal. owners.do_not_mix is DERIVED from this on
    // write so the picker triangle and BookingForm's seed keep working (0033).
    keepApart: boolean("keep_apart"),
    // field name -> the owner_contacts.id that last wrote it, e.g.
    // {"name": 42552, "breed": 42552, "keepApart": 42552}. Per FIELD because a
    // contact can confirm a breed and not a name (AC1). A desk edit DELETES its
    // key — the mark claims the household told us this, and after she types it
    // is no longer true (0033).
    confirmedBy: jsonb("confirmed_by"),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
  },
  (t) => [
    check("dogs_size_check", sql`${t.size} IN ('big','little','mix','house','unknown')`),
    index("idx_dogs_owner").on(t.ownerId),
    index("idx_dogs_name").on(t.name),
  ],
);

// --- bookings ---------------------------------------------------------

export const bookings = pgTable(
  "bookings",
  {
    id: text("id").primaryKey(),
    ownerId: text("owner_id")
      .notNull()
      .references(() => owners.id, { onDelete: "restrict" }),
    checkIn: date("check_in").notNull(),
    checkOut: date("check_out").notNull(),
    area: text("area").notNull(),
    dropOff: text("drop_off").notNull(),
    collection: text("collection").notNull(),
    ratePerNight: numeric("rate_per_night", { precision: 10, scale: 2 }),
    // MONEY RECEIVED TO DATE — not "the deposit", whatever the name says. It
    // arrived as the import's mapping of the spreadsheet's "Paid" column
    // (scripts/import-bookings.ts) and every read since has treated it as
    // "what's been paid" (amountDue = total − deposit). Since migration 0017 it
    // is a DENORMALISED MIRROR of SUM(payments.amount) for this booking,
    // recomputed by the API on every payment write — never written directly.
    // The name is kept because the Due column, the desk, the sort key and the
    // modal all read it; renaming it is its own PR.
    deposit: numeric("deposit", { precision: 10, scale: 2 })
      .notNull()
      .default("0"),
    // Denormalised — computable from nights × rate × dogs but used for
    // indexed sort/filter. Recomputed on every save. See data-model-skeleton.md.
    total: numeric("total", { precision: 10, scale: 2 })
      .notNull()
      .default("0"),
    notes: text("notes"),
    /** WHO WROTE THE NOTE (0037) — a customer's request; cleared when the desk changes it. */
    notesBy: integer("notes_by").references(() => ownerContacts.id, { onDelete: "set null" }),
    mixOk: boolean("mix_ok").notNull().default(true),
    // Care flags (staff-food-at-house). Per booking, admin-set in the modal;
    // each is the SOURCE for its staff Today briefing card + admin flag chip.
    // Keep-apart is the existing mixOk above (checkbox on = mix_ok false).
    foodAtHouse: boolean("food_at_house").notNull().default(false),
    meds: boolean("meds").notNull().default(false),
    status: text("status").notNull(),
    // WHERE the booking was made (migration 0022). NOT NULL DEFAULT 'desk',
    // which is true of every booking that existed before a portal did. The fact
    // behind a stay that "began at Requested" — provenance, not a badge.
    source: text("source").notNull().default("desk"),
    // The BB-XXXX a customer quotes on a bank transfer, minted on first need.
    // NULLABLE and UNIQUE: Postgres treats NULLs as distinct, so twenty-two
    // thousand reference-less bookings coexist under it.
    reference: text("reference"),
    paymentStatus: text("payment_status").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    // Bumped on every row change by the set_updated_at BEFORE UPDATE trigger
    // (migration 0016) — the "Last updated" column's sort source. Backfilled to
    // created_at for pre-existing rows.
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    // `mix` retired for `other` (0030, dogs-in-areas AC4). `other` is DERIVED
    // from the dogs' own areas on every save and never chosen, which is what
    // stops it meaning what `mix` came to mean — 24 of the 146 `mix` bookings
    // had exactly ONE dog, so the word had drifted to "somebody was unsure".
    check("bookings_area_check", sql`${t.area} IN ('big','little','house','other')`),
    check("bookings_drop_off_check", sql`${t.dropOff} IN ('am','pm')`),
    check("bookings_collection_check", sql`${t.collection} IN ('am','pm')`),
    check(
      "bookings_status_check",
      sql`${t.status} IN ('unconfirmed','confirmed','arrived','collected','cancelled','requested','declined')`,
    ),
    check("bookings_source_check", sql`${t.source} IN ('desk','portal')`),
    check(
      "bookings_payment_status_check",
      sql`${t.paymentStatus} IN ('unpaid','partial','paid')`,
    ),
    check("bookings_date_range_check", sql`${t.checkOut} >= ${t.checkIn}`),
    index("idx_bookings_status").on(t.status),
    index("idx_bookings_check_in").on(t.checkIn),
    index("idx_bookings_check_out").on(t.checkOut),
    index("idx_bookings_owner").on(t.ownerId),
    // Composite for the default list sort (check_in DESC, id DESC) — makes it a
    // pure index-ordered (index-only) scan instead of an incremental sort as
    // same-day stays accumulate. Raw SQL (not .desc()) so the null ordering is
    // DESC NULLS FIRST: that is what `ORDER BY ... DESC` defaults to, and an
    // index built NULLS LAST would NOT be used for it. See docs: browse-bookings-readonly.
    index("idx_bookings_check_in_id").on(sql`${t.checkIn} DESC, ${t.id} DESC`),
    // Composite for the "Last updated" sort (updated_at DESC, id DESC) — same
    // index-ordered-scan rationale as idx_bookings_check_in_id above.
    index("idx_bookings_updated_id").on(sql`${t.updatedAt} DESC, ${t.id} DESC`),
    // GIST daterange index for capacity-overlap queries — non-negotiable
    // per performance-budget.md.
    index("idx_bookings_date_range")
      .using("gist", sql`daterange(${t.checkIn}, ${t.checkOut}, '[]')`),
    // UNIQUE, and nullable — see the `reference` comment above.
    unique("bookings_reference_unique").on(t.reference),
    // PARTIAL, on the requested rows alone (§7). The desk's needs-attention read
    // asks for exactly these and they are the rare case, so it stays small.
    index("idx_bookings_requested").on(t.status).where(sql`${t.status} = 'requested'`),
  ],
);

// --- booking_dogs -----------------------------------------------------

export const bookingDogs = pgTable(
  "booking_dogs",
  {
    bookingId: text("booking_id")
      .notNull()
      .references(() => bookings.id, { onDelete: "cascade" }),
    dogId: text("dog_id")
      .notNull()
      .references(() => dogs.id, { onDelete: "restrict" }),
    rateOverride: numeric("rate_override", { precision: 10, scale: 2 }),
    // WHERE THIS DOG IS, on THIS booking (dogs-in-areas AC1). Nullable because
    // history holds bookings nobody can now assign — ten dogs on five of them,
    // counted rather than estimated. AC8 is what makes that safe: an unplaced
    // dog counts on the kennel line and against no area line, never guessed
    // into a room. Capacity reads this column rather than `dogs.size` (AC6).
    area: text("area"),
    // WHICH DOGS THIS STAY'S CARE FLAGS ARE ABOUT (per-dog-care-flags, 0034).
    //
    // `bookings.food_at_house`, `bookings.meds` and `bookings.mix_ok` are one
    // tick each for the whole booking, so a household with three dogs put
    // "Medication" on the staff brief and named nobody. These say who.
    //
    // NULL MEANS NOBODY HAS SELECTED — not "not this dog". Every booking taken
    // before 0034 has nulls across the row, and the brief reads that as the
    // words it has always had rather than inventing a name.
    foodAtHouse: boolean("food_at_house"),
    // `needs_meds`, not `meds`: `dogs.meds` is the instructions TEXT and this
    // is a selection BOOLEAN. One join reading both would have two fields with
    // one name and no way to tell which won.
    needsMeds: boolean("needs_meds"),
    keepApart: boolean("keep_apart"),
    /**
     * What this dog's medication was AT CHECK-IN.
     *
     * The instructions themselves live on `dogs.meds` and there is one of them,
     * so the household modal and the booking cannot disagree. A stay reads that
     * record live while it is still ahead of us — which is how a household edit
     * carries forward to every future booking with nothing copied — and stamps
     * this column at arrival, after which later edits cannot reach it.
     *
     * A completed stay is a record of what the carer actually followed. See
     * decision/per-dog-care-flags-two-clocks.md.
     */
    medsAtArrival: text("meds_at_arrival"),
    /**
     * WHO WROTE THIS STAY'S MEDICATION (0036). The contact whose words
     * `meds_at_arrival` holds — set by a customer's request, kept by a desk
     * save that leaves the words alone, cleared by one that changes them.
     */
    medsBy: integer("meds_by").references(() => ownerContacts.id, { onDelete: "set null" }),
    /** WHO ASKED FOR THIS DOG TO BE KEPT APART (0037) — cleared when the desk changes the answer. */
    apartBy: integer("apart_by").references(() => ownerContacts.id, { onDelete: "set null" }),
  },
  (t) => [
    primaryKey({ columns: [t.bookingId, t.dogId] }),
    index("idx_booking_dogs_dog").on(t.dogId),
    index("idx_booking_dogs_area").on(t.area),
    check("booking_dogs_area_check", sql`${t.area} IS NULL OR ${t.area} IN ('big','little','house')`),
  ],
);

// --- payments ---------------------------------------------------------
// One row = one sum of money received against one booking, on one date. The
// table exists because nothing in the system recorded WHEN money arrived: the
// Finances page reported SUM(bookings.total) — value billed — and there was no
// counterpart for value received, which is why the app could report a €57k July
// against a bank account that had seen nothing of the sort
// (docs/decision/payments-ledger.md).
//
// This is the authority. bookings.deposit and bookings.payment_status are both
// recomputed from it (payments/ledger.ts); neither is ever written on its own.
//
// `amount` is CHECKed non-zero rather than positive, and the distinction is
// deliberate. Every user-facing path (modal, desk) goes through
// validatePaymentInput, which refuses anything <= 0: a mistaken payment is
// DELETEd, not negated, so the ledger cannot be made to sum correctly out of
// two wrong rows. The one exception is the 0017 backfill — 17 imported
// bookings carry a NEGATIVE "Paid" in the source sheet (arrears carried forward
// from a previous unpaid stay), and refusing those would silently change 17
// balances the day this shipped. They import as they are; nothing new can.
export const payments = pgTable(
  "payments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    bookingId: text("booking_id")
      .notNull()
      .references(() => bookings.id, { onDelete: "cascade" }),
    amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
    // The date the money ARRIVED — the whole point of the table. Grouped by
    // month for the Finances page's Received basis. A date, not a timestamp:
    // nobody knows the hour, and pretending otherwise invites a timezone bug.
    receivedOn: date("received_on").notNull(),
    source: text("source").notNull(),
    // WHICH FIGURE this row belongs to — the booking modal shows the ledger as a
    // deposit and a remainder. Separate from `source` deliberately: see the
    // PAYMENT_KINDS comment in enums.ts for why inferring the split from
    // `source` let the settle-undo delete hand-typed money.
    kind: text("kind").notNull().default("deposit"),
    // Replay guard for the modal's write. One UUID per form submission: if the
    // INSERT commits but its response is lost, the retry carries the same key,
    // conflicts, and records nothing. Necessary because two identical payments
    // are perfectly legitimate — no natural key can tell a duplicate from a
    // genuine second payment of the same amount on the same day. NULL for desk
    // and import rows: settle is already idempotent through its own guard, and
    // the backfill runs once.
    clientKey: uuid("client_key"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    // CLEARED as an entry error, and by whom (migration 0019). A payment typed in
    // error is voided, never deleted and never negated: deleting leaves no trace,
    // which is what would force a confirmation step, and a reversing negative row
    // would read as a refund — which is what a negative amount already means here.
    //
    // The column's `amount <> 0` CHECK is therefore untouched: clearing a payment
    // stamps this instead of storing a zero. Every SUM over this table excludes a
    // voided row; the booking detail is the one read that keeps them, so the modal
    // can show that the correction happened.
    voidedAt: timestamp("voided_at", { withTimezone: true }),
    voidedBy: text("voided_by"),
    // Ticked when Patricia sees the money on the bank statement (migration
    // 0023). A screenshot-confirmed deposit and a bank-seen one are different
    // facts, and this ledger exists because money was once recorded on a basis
    // nobody could name. Nothing writes it yet — desk-self-service-column does.
    reconciledAt: timestamp("reconciled_at", { withTimezone: true }),
    // THE SCREENSHOT THIS ROW CAME FROM (migration 0039, deposit-proof AC10).
    // Nullable: desk-typed and imported money has none. Set by Accept on a
    // proof, or by linking a proof to money the desk recorded by hand (AC12a).
    proofId: uuid("proof_id").references(() => proofs.id, { onDelete: "set null" }),
  },
  (t) => [
    check("payments_amount_check", sql`${t.amount} <> 0`),
    // ONE VERDICT, ONE PAYMENT (AC21): two Accepts racing on one screenshot
    // cannot both land a row. Partial, so the many proof-less rows coexist.
    uniqueIndex("payments_proof_id_unique").on(t.proofId).where(sql`${t.proofId} IS NOT NULL`),
    check(
      "payments_source_check",
      sql`${t.source} IN ('import','modal','desk','portal')`,
    ),
    check("payments_kind_check", sql`${t.kind} IN ('deposit','remainder')`),
    index("idx_payments_booking").on(t.bookingId),
    // The Received basis groups the whole table by month; this is the index that
    // read walks. received_on first — it is the range predicate.
    index("idx_payments_received_on").on(t.receivedOn, t.bookingId),
    // UNIQUE, so ON CONFLICT (client_key) DO NOTHING has something to conflict
    // on. Postgres treats NULLs as distinct, so the many key-less desk and
    // import rows coexist freely under it.
    unique("payments_client_key_unique").on(t.clientKey),
    // PARTIAL, on the live rows only. Every figure computed from this table is
    // scoped to one booking and filters `voided_at IS NULL`, so that is exactly
    // what the index covers — and voided rows being the rare case keeps it small.
    index("idx_payments_live")
      .on(t.bookingId)
      .where(sql`${t.voidedAt} IS NULL`),
  ],
);

// --- idempotency_keys -------------------------------------------------
// Replay-safe create (create-booking AC7). One row per distinct create:
// the client-supplied key maps to the booking it produced, so a retry of
// the same POST returns the original booking instead of inserting a
// duplicate (docs/architecture/create-booking/schema.md). The PK on `key`
// is the W1a lookup index AND the uniqueness guard that serialises two
// concurrent retries of the same key. `request_method`/`request_path`
// scope a key to the operation it was first used on so one table can serve
// the whole mutating surface later (POST-only for this story). FK to
// bookings ON DELETE CASCADE so deleting a booking never strands its key.

export const idempotencyKeys = pgTable("idempotency_keys", {
  key: text("key").primaryKey(),
  requestMethod: text("request_method").notNull(),
  requestPath: text("request_path").notNull(),
  // ONE OF TWO TARGETS since 0021: a key names the booking OR the household it
  // produced, never both and never neither (idempotency_keys_one_target).
  bookingId: text("booking_id").references(() => bookings.id, { onDelete: "cascade" }),
  ownerId: text("owner_id").references(() => owners.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// --- staff ------------------------------------------------------------

export const staff = pgTable(
  "staff",
  {
    id: text("id").primaryKey(),
    name: citext("name").notNull(),
    role: text("role"),
    // The login email that links this staff record to a sign-in identity, so a
    // `staff`-role user can be resolved to their own shifts (staff-self-view).
    // citext = case-insensitive match against the session email. Nullable: a
    // staff record without a login can't sign in (no self-view). UNIQUE so one
    // login maps to exactly one person; Postgres treats NULLs as distinct, so
    // any number of un-linked records coexist.
    email: citext("email"),
    // The mobile the staff daily brief (WhatsApp) is sent to — the gate the whole
    // messaging sequence waits on (Story 0). Nullable: NULL = no number on file
    // yet, so nothing downstream can message this person. Stored as a normalised
    // E.164 number (leading "+", country code, digits); the API rejects local-only
    // numbers on write, so a stored value is always internationally dialable.
    phone: text("phone"),
    // Nullable: NULL means the helper is on the minimum wage (settings.minimum_wage)
    // — the rate is inherited, so an annual minimum-wage rise is a single edit.
    // A number is a custom rate that overrides the minimum (e.g. a sub-minimum
    // trainee rate). Effective rate = rate ?? minimum_wage.
    rate: numeric("rate", { precision: 10, scale: 2 }),
    holsUsed: integer("hols_used").notNull().default(0),
    holsTotal: integer("hols_total").notNull().default(0),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
  },
  (t) => [unique("staff_email_unique").on(t.email)],
);

// App-wide key/value settings. First key: minimum_wage (the standard hourly rate
// helpers inherit when their own rate is NULL). Kept generic so later config
// (e.g. capacity, default holiday allowance) lands here without a new table.
export const settings = pgTable("settings", {
  key: text("key").primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// --- shifts -----------------------------------------------------------

export const shifts = pgTable(
  "shifts",
  {
    id: integer("id").primaryKey().generatedByDefaultAsIdentity(),
    date: date("date").notNull(),
    slot: integer("slot").notNull(),
    staffId: text("staff_id").references(() => staff.id, {
      onDelete: "set null",
    }),
    kind: text("kind").notNull(),
  },
  (t) => [
    check("shifts_slot_check", sql`${t.slot} BETWEEN 0 AND 7`),
    check("shifts_kind_check", sql`${t.kind} IN ('am','pm')`),
    unique("shifts_date_slot_unique").on(t.date, t.slot),
    index("idx_shifts_staff_date").on(t.staffId, t.date),
    index("idx_shifts_date").on(t.date),
  ],
);

// --- pay_marks --------------------------------------------------------

export const payMarks = pgTable(
  "pay_marks",
  {
    staffId: text("staff_id")
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    weekStart: date("week_start").notNull(),
    paidAt: timestamp("paid_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [primaryKey({ columns: [t.staffId, t.weekStart] })],
);

// --- holidays ---------------------------------------------------------

export const holidays = pgTable(
  "holidays",
  {
    id: integer("id").primaryKey().generatedByDefaultAsIdentity(),
    staffId: text("staff_id")
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    fromDate: date("from_date").notNull(),
    toDate: date("to_date").notNull(),
    kind: text("kind").notNull(),
    status: text("status").notNull(),
    note: text("note"),
  },
  (t) => [
    check("holidays_kind_check", sql`${t.kind} IN ('annual','sick','unpaid')`),
    check("holidays_status_check", sql`${t.status} IN ('approved','pending')`),
    check("holidays_date_range_check", sql`${t.toDate} >= ${t.fromDate}`),
    index("idx_holidays_staff_dates").on(t.staffId, t.fromDate, t.toDate),
    index("idx_holidays_range")
      .using("gist", sql`daterange(${t.fromDate}, ${t.toDate}, '[]')`),
  ],
);

// --- dogs_by_date -----------------------------------------------------

export const dogsByDate = pgTable("dogs_by_date", {
  date: date("date").primaryKey(),
  count: integer("count").notNull().default(0),
});

// --- activity events (admin metrics) ---------------------------------
// One row per tracked event: a sign-in, a page view (with dwell), or a
// mutating action. Powers the admins-only Activity view. Append-only; the
// admin dashboard aggregates over it. No retention purge by product decision
// (kept indefinitely — recorded as the maximal-retention posture).
export const activityEvents = pgTable(
  "activity_events",
  {
    // bigint, not integer: append-only with no retention purge, so the PK must
    // not carry a 2.1B ceiling it can silently grow into. Cheap now, an outage
    // later. `mode: "number"` — event ids never approach 2^53.
    id: bigint("id", { mode: "number" }).primaryKey().generatedByDefaultAsIdentity(),
    // Who acted — the session email (citext, matches staff.email). Held as the
    // identity even if the staff record is later renamed or removed.
    actorEmail: citext("actor_email").notNull(),
    // The allowlist role at the time of the event (admin / owner / helper).
    role: text("role"),
    // 'login' | 'page_view' | 'action'.
    kind: text("kind").notNull(),
    // page_view → the section key ('today', 'bookings', …). action →
    // "<entity>.<verb>" ('booking.create', 'owner.update'). Null for login.
    label: text("label"),
    // action → the affected record id (e.g. a booking id), when known. Null otherwise.
    target: text("target"),
    // page_view → approximate dwell in ms (active time between navigations).
    // Null for login/action.
    durationMs: integer("duration_ms"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Per-user, newest-first — the drill-down's core access pattern.
    index("idx_activity_actor_created").on(t.actorEmail, t.createdAt),
    // Whole-set by time — the overview/date-window scans.
    index("idx_activity_created").on(t.createdAt),
  ],
);

// --- staff_brief_jobs -------------------------------------------------

// The durable outbox for the automated staff daily brief (messaging Story 1).
// One row = one staff member's brief for one day. This is the WHOLE messaging
// data model for the staff-brief slice — the generic events/templates/recipients
// spine from the parked vision stays parked until a second message type earns it.
export const staffBriefJobs = pgTable(
  "staff_brief_jobs",
  {
    // DB-generated uuid, NOT a max(id)+1 text id: this outbox is machine-enqueued
    // and can be concurrent, and max(id)+1 races — two enqueues read the same max
    // and collide on the PK, dropping a brief (the (brief_date, staff_id) conflict
    // target doesn't cover a PK conflict). gen_random_uuid never collides.
    id: uuid("id").primaryKey().defaultRandom(),
    // The day the brief is for (local Dublin calendar day).
    briefDate: date("brief_date").notNull(),
    // FK for referential integrity. The onDelete cascade only fires on a HARD
    // delete, which never happens — staff removal is a SOFT delete (archived_at).
    // So archival is handled explicitly: the archive route suppresses pending
    // briefs (cancelPendingBriefsForStaff) and the claim query excludes archived
    // staff, so a former helper is never messaged.
    staffId: text("staff_id")
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    // Snapshot of staff.phone at enqueue. NULL ⇒ the staff member had no number
    // on file, so the job is `suppressed` (traceable, never sent). Enforced
    // non-null at the `sending` transition by the lifecycle module, not by the
    // column — the suppression record must be allowed to exist.
    recipientPhone: text("recipient_phone"),
    // The message body, filled by Story 3's render. NULL on a suppressed job.
    renderedText: text("rendered_text"),
    // queued → sending → sent | failed ; or suppressed at enqueue. text + CHECK,
    // mirroring shifts.kind — not a pg enum.
    status: text("status").notNull().default("queued"),
    attempts: integer("attempts").notNull().default(0),
    // The sender's lease. A `sending` job whose claim_expires_at has passed is
    // reclaimable — the claim query picks it up again, so a crashed sender can't
    // strand a brief forever.
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    claimExpiresAt: timestamp("claim_expires_at", { withTimezone: true }),
    // Minted fresh on every claim. A terminal transition (sent/failed) must
    // present the current token, so a slow sender whose lease was reclaimed by a
    // second sender can't complete the second sender's attempt — the double-send
    // fence at the completion layer.
    claimToken: uuid("claim_token"),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    error: text("error"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      "staff_brief_jobs_status_check",
      sql`${t.status} IN ('queued','sending','sent','failed','suppressed')`,
    ),
    // Idempotency: one brief per staff member per day, DB-enforced — enqueue is
    // INSERT … ON CONFLICT (brief_date, staff_id) DO NOTHING.
    unique("staff_brief_jobs_date_staff_unique").on(t.briefDate, t.staffId),
    // The claim/reclaim query: due = queued OR (sending AND lease expired).
    index("idx_staff_brief_jobs_claim").on(t.status, t.claimExpiresAt),
  ],
);

// --- viewings ---------------------------------------------------------
//
// An enquiry with an appointment attached — the record of somebody who exists
// BEFORE a household does. Not a booking with fewer fields, and deliberately not
// a `kind` column on `bookings`: 32 SQL sites across 25 API modules filter on
// nothing but `status <> 'cancelled'`, and a non-stay row among them reads as
// revenue the moment one is missed.
//
// Nothing else in this file references it. The dependency runs one way only,
// from a viewing to the booking it eventually produced.
// See docs/architecture/log-a-viewing/.

export const viewings = pgTable(
  "viewings",
  {
    // `v<N>`, minted by nextId() — the same convention as bookings' `b<N>` and
    // owners' `o<N>`.
    id: text("id").primaryKey(),
    // The one thing a viewing cannot be saved without. It is the row's lead on
    // the desk, where the dog names go on every other row — a viewing with no
    // name is not a record of anybody.
    contactName: text("contact_name").notNull(),
    // Offered but never required (log-a-viewing AC2). Blank is stored NULL.
    phone: text("phone"),
    // A DATE, not a timestamp: a viewing is on a day. The app has been handed a
    // timestamp by an API where a date column was expected before, which is what
    // dateOnly() exists to survive; storing the right type is the cheaper half.
    viewingOn: date("viewing_on").notNull(),
    // morning | afternoon — the precision a telephone call actually yields.
    slot: text("slot").notNull(),
    // expected | visited | no_show. Written 'expected' on create and never taken
    // from the client: AC4 is a guarantee, and a guarantee with a client
    // override is a default.
    status: text("status").notNull().default("expected"),
    // The dogs as DESCRIBED on the telephone — [{name, breed}], both nullable.
    // Not booking_dogs: those reference real animals with real records. These
    // are what somebody said, and no access pattern ever reads them apart from
    // their viewing, so they are stored with it rather than joined to it.
    // The CHECK guarantees an array and nothing more; the API's validator is the
    // contract for what is inside. See ADR-2.
    dogs: jsonb("dogs").notNull().default(sql`'[]'::jsonb`),
    notes: text("notes"),
    // THE CONVERSION FACT, and it is not a status. `status` answers "did the
    // visit happen"; this answers "did it produce a customer". Two orthogonal
    // questions in two fields, rather than one four-member enum that would make
    // "how many viewings did we hold?" a query over three of the four.
    //
    // SET NULL, never CASCADE: deleting a booking must not delete the record
    // that somebody came to look round. The visit happened; only the customer it
    // produced went away.
    bookingId: text("booking_id").references(() => bookings.id, { onDelete: "set null" }),
    // The household this viewing BECAME (0021). Read by Book on a viewing whose
    // household exists and whose booking does not — the walkthrough's own
    // second press — so it opens a booking for that household rather than
    // creating it again. SET NULL, never CASCADE, for the same reason as above.
    ownerId: text("owner_id").references(() => owners.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    // NO set_updated_at TRIGGER, deliberately (ADR-6). Migration 0016 installed
    // it on bookings and owners because both feed a sortable "Last updated"
    // column; no viewings surface sorts by this one, so the trigger would be
    // machinery serving no read. Adding it later is one idempotent statement.
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check("viewings_status_check", sql`${t.status} IN ('expected','visited','no_show')`),
    check("viewings_slot_check", sql`${t.slot} IN ('morning','afternoon')`),
    check("viewings_dogs_is_array", sql`jsonb_typeof(${t.dogs}) = 'array'`),
    // The only filtered read this table has: viewings in a date window, sorted
    // by the same column. Not partial — a Visited viewing still shows on the
    // desk, greyed out, so a `WHERE status = 'expected'` index would miss the
    // rows the desk actually renders.
    index("idx_viewings_on").on(t.viewingOn),
  ],
);

// --- the customer portal (migrations 0026, 0027) -----------------------
//
// Four tables that nothing on the desk reads yet. They are here now, rather
// than with the story that first reads them, because portal-foundations exists
// precisely so that no later story is quietly also a migration.

// --- proofs -----------------------------------------------------------
// One row per ATTEMPT. A customer who sends the wrong screenshot three times
// leaves three rows, and the three-tries rule is enforced by counting them —
// the alternative, one row updated in place, loses the fact that a first
// attempt happened, which is exactly what Patricia rings people about.
export const proofs = pgTable(
  "proofs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // A payment proof names a booking; a card names a dog. Each kind fills one
    // of the two, held by proofs_target_check below.
    bookingId: text("booking_id").references(() => bookings.id, { onDelete: "cascade" }),
    dogId: text("dog_id").references(() => dogs.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(),
    // The R2 object key. NEVER a public URL — GET /api/proofs/:id/image
    // authenticates the owning household or the desk and streams it (§9).
    r2Key: text("r2_key").notNull(),
    mime: text("mime").notNull(),
    bytes: integer("bytes").notNull(),
    uploadedBy: text("uploaded_by").notNull(),
    // NOT the allowlist role: the desk's own drop-zone upload is 'desk'
    // whichever staff role did it. The question this answers is "did this come
    // from the household or from us".
    uploadedRole: text("uploaded_role").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    // What the read service made of the image. jsonb because the shape differs
    // per kind, and a vision model's output is not a schema this table pins.
    read: jsonb("read"),
    matched: boolean("matched").notNull().default(false),
    attempt: integer("attempt").notNull().default(1),
    // A card's dates as the CUSTOMER typed them — a claim, not a fact. Accept
    // is what promotes a claim onto the dog.
    claimedValidUntil: text("claimed_valid_until"),
    claimedKcUntil: text("claimed_kc_until"),
    verifiedAt: timestamp("verified_at", { withTimezone: true }),
    verifiedBy: text("verified_by"),
    rejectedAt: timestamp("rejected_at", { withTimezone: true }),
    rejectedBy: text("rejected_by"),
    rejectedReason: text("rejected_reason"),
    undoneAt: timestamp("undone_at", { withTimezone: true }),
  },
  (t) => [
    check("proofs_kind_check", sql`${t.kind} IN ('deposit','remainder','card')`),
    check("proofs_uploaded_role_check", sql`${t.uploadedRole} IN ('customer','desk')`),
    check(
      "proofs_target_check",
      sql`(${t.kind} = 'card' AND ${t.dogId} IS NOT NULL) OR (${t.kind} <> 'card' AND ${t.bookingId} IS NOT NULL)`,
    ),
    // §9's 12 MB ceiling, at the table as well as at the route — so no path can
    // write a row claiming an object larger than one that may be stored.
    check("proofs_bytes_check", sql`${t.bytes} > 0 AND ${t.bytes} <= 12582912`),
    index("idx_proofs_booking_created").on(t.bookingId, t.createdAt),
    index("idx_proofs_dog").on(t.dogId),
    // ONE SCREENSHOT IN FLIGHT PER BOOKING (deposit-proof AC18, migration
    // 0039): a payment proof with no verdict blocks another of either kind,
    // and the database says so even when two uploads race the route's check.
    uniqueIndex("proofs_one_pending_payment_proof")
      .on(t.bookingId)
      .where(sql`${t.kind} <> 'card' AND ${t.verifiedAt} IS NULL AND ${t.rejectedAt} IS NULL`),
  ],
);

// --- portal_invites ---------------------------------------------------
// One-use, time-boxed registration links. The token IS the primary key: a
// lookup by token is the only read there is, so a surrogate id would have
// bought a second index and nothing else.
export const portalInvites = pgTable(
  "portal_invites",
  {
    token: text("token").primaryKey(),
    ownerId: text("owner_id")
      .notNull()
      .references(() => owners.id, { onDelete: "cascade" }),
    // Nullable: a link can be issued to a household before the desk knows which
    // of its people will use it.
    contactId: integer("contact_id").references(() => ownerContacts.id, { onDelete: "set null" }),
    issuedBy: text("issued_by").notNull(),
    issuedAt: timestamp("issued_at", { withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    // Burned at the OAuth callback that mints the allowlist entry. WHEN was
    // household-app-access' open question and is now answered: on COMPLETED
    // SIGN-IN, never on opening the link, so a mail scanner that prefetches it
    // costs the customer nothing.
    usedAt: timestamp("used_at", { withTimezone: true }),
    // The verified email that completed the sign-in, stamped in the same write
    // as `usedAt` (migration 0031). AC11 tells a second person the link is
    // already used AND BY WHOM, which the row could not say before.
    usedByEmail: text("used_by_email"),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
  },
  (t) => [index("idx_portal_invites_owner").on(t.ownerId)],
);

// --- desk_notifications -----------------------------------------------
// The bell. One row per R-DESK-15 event, and NOT a projection: "this was read,
// by this person, at this time" is a fact no other table holds.
export const deskNotifications = pgTable(
  "desk_notifications",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    kind: text("kind").notNull(),
    bookingId: text("booking_id").references(() => bookings.id, { onDelete: "cascade" }),
    dogId: text("dog_id").references(() => dogs.id, { onDelete: "cascade" }),
    proofId: uuid("proof_id").references(() => proofs.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    readAt: timestamp("read_at", { withTimezone: true }),
    readBy: text("read_by"),
    // 0042 — who sent it (the sign-in) and whose household; NULL on a row written before.
    actorEmail: citext("actor_email"),
    ownerId: text("owner_id").references(() => owners.id, { onDelete: "cascade" }),
  },
  (t) => [index("idx_desk_notifications_read_created").on(t.readAt, t.createdAt)],
);

// --- push_subscriptions ------------------------------------------------
// One phone per SUBJECT, for every kind of notification (roster-change-notify,
// 0044). The subject is who the phone belongs to — `staff:<id>` today;
// `account:<email>` / `contact:<id>` when Patricia's and the customers' push
// arrive. PK = subject (a second phone replaces the first); endpoint UNIQUE
// (one phone is never filed under two subjects). No FK: the owning code
// removes the phone when its row goes. Keys as the browser hands them.
export const pushSubscriptions = pgTable(
  "push_subscriptions",
  {
    subject: text("subject").primaryKey(),
    endpoint: text("endpoint").notNull().unique(),
    p256dh: text("p256dh").notNull(),
    auth: text("auth").notNull(),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [check("push_subscriptions_subject_check", sql`${t.subject} ~ '^[a-z]+:.+$'`)],
);

// --- shift_alerts ---------------------------------------------------------
// The shift-alert queue and its audit (roster-change-notify, 0045): one row per
// helper per burst of rota edits — `before` is their days when the burst began,
// `due_at` the last edit + 60 s. The partial unique index keeps one QUEUED row
// per helper so a further edit extends it; the minute tick resolves it.
export const shiftAlerts = pgTable(
  "shift_alerts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    staffId: text("staff_id").notNull().references(() => staff.id, { onDelete: "cascade" }),
    status: text("status").notNull(),
    before: jsonb("before").notNull(),
    windowFrom: date("window_from").notNull(),
    windowTo: date("window_to").notNull(),
    dueAt: timestamp("due_at", { withTimezone: true }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    lines: jsonb("lines"),
    error: text("error"),
  },
  (t) => [
    check("shift_alerts_status_check", sql`${t.status} IN ('queued', 'sending', 'sent', 'skipped', 'failed')`),
    uniqueIndex("shift_alerts_one_queued").on(t.staffId).where(sql`${t.status} = 'queued'`),
    // The minute tick's predicate; the table is also the audit and only grows (review, PR #278).
    index("shift_alerts_drain").on(t.dueAt).where(sql`${t.status} IN ('queued', 'sending')`),
  ],
);

// --- capacity_blocks --------------------------------------------------
// Patricia's DATED override of a standing per-size limit. No row for a date
// means the standing setting applies; `limit = 0` means closed to that size for
// the period, which is why the CHECK is >= 0 rather than > 0.
//
// `line` is kennel | big | little. The two size lines are the only two SIZES
// lets the app create; a dog stored as mix/house/unknown is sheet-import
// legacy and counts on the KENNEL line and against no size line (AC16) — which
// is precisely why the kennel line exists and why the column is not `size`.
// Freddi made the kennel line overridable on 2026-09-10.
//
// OVERLAPS ARE REFUSED AT THE API, not by an exclusion constraint. AC14 asks
// for an error naming the range it collides with — a sentence only the route
// can write. A constraint would return a Postgres string to Patricia's screen.
export const capacityBlocks = pgTable(
  "capacity_blocks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // `line`, not `size` — the kennel line is not a dog size (migration 0029).
    line: text("line").notNull(),
    startsOn: date("starts_on").notNull(),
    // INCLUSIVE — "no more bigs 12–19 Dec" closes the 19th too. The counting
    // helper reads [startsOn, endsOn], the way Patricia says it.
    endsOn: date("ends_on").notNull(),
    // The RED line: at it, the customer cannot submit. 0 means closed.
    hardLimit: integer("hard_limit").notNull(),
    // The AMBER line: between soft and hard the customer is warned ("limited
    // space available") and may still book. NULLABLE — a closed week wants no
    // warning threshold, and inventing one paints an amber day inside it.
    softLimit: integer("soft_limit"),
    note: text("note"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check("capacity_blocks_line_check", sql`${t.line} IN ('kennel','big','little','house')`),
    check("capacity_blocks_hard_limit_check", sql`${t.hardLimit} >= 0`),
    // Soft above hard deletes the amber band silently and is a typo every time
    // — deliberately NOT treated like a size line above the kennel line, which
    // is a legitimate configuration where one line simply never binds.
    check(
      "capacity_blocks_soft_below_hard_check",
      sql`${t.softLimit} IS NULL OR (${t.softLimit} >= 0 AND ${t.softLimit} <= ${t.hardLimit})`,
    ),
    check("capacity_blocks_range_check", sql`${t.endsOn} >= ${t.startsOn}`),
    index("idx_capacity_blocks_line_range").on(t.line, t.startsOn, t.endsOn),
    // NOT DECLARABLE HERE, and present in the database: migration 0028 adds
    //   EXCLUDE USING gist (line WITH =, daterange(starts_on, ends_on, '[]') WITH &&)
    // so two blocks of one line cannot cover the same day (0028; the column was
    // `size` when that constraint was written and Postgres carried it across
    // 0029's rename). Drizzle has no
    // exclusion-constraint builder, so this comment is the mirror. The route's
    // own overlap check stays — it writes the message that names the range you
    // collided with; this is the backstop that makes the message unavoidable
    // under concurrency (PR #204 review).
  ],
);

// --- handover_days ----------------------------------------------------
// Patricia's restricted drop-off and collection days (handover-days): a date or
// a run of dates where customers may only hand over in the afternoon, or not at
// all. Drop-offs and collections share the rule (Freddi, 2026-10-12). A row
// beats the weekly rule (`handover_closed_weekly`) on every day it covers.
//
// For CUSTOMERS only — the desk's own booking form reads none of this.
export const handoverDays = pgTable(
  "handover_days",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // INCLUSIVE at both ends, like capacity_blocks: "24–25 Dec" closes the 25th.
    startsOn: date("starts_on").notNull(),
    endsOn: date("ends_on").notNull(),
    // pm_only | closed — HANDOVER_DAY_RULES.
    rule: text("rule").notNull(),
    note: text("note"),
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check("handover_days_rule_check", sql`${t.rule} IN ('pm_only','closed')`),
    check("handover_days_range_check", sql`${t.endsOn} >= ${t.startsOn}`),
    index("idx_handover_days_range").on(t.startsOn, t.endsOn),
    // NOT DECLARABLE HERE, and present in the database: migration 0040 adds
    //   EXCLUDE USING gist (daterange(starts_on, ends_on, '[]') WITH &&)
    // so no two rows cover one day. The route's overlap check writes the
    // sentence; this is the backstop under concurrency, as for capacity_blocks.
  ],
);
